import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Trash2, Edit2, User, Mail, Shield, Link2, AlertCircle, Eye, EyeOff, LogIn, ArrowLeft, ChevronDown, ChevronRight, Users, Info } from 'lucide-react';
import authService from '../../services/authService';
import userService from '../../services/userService';
import QuickBooksService from '../../services/quickbooksService';
import { crudOperations } from '../../services/api';
import AdminService from '../../services/adminService';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import { useToast } from '../Toast';

const UserManagement = ({ currentUser, userProfile }) => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qbConnections, setQbConnections] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [sortedUsers, setSortedUsers] = useState([]);
  const toast = useToast();
  const [oauthCompletedSessions, setOauthCompletedSessions] = useState(new Set());
  const [connectingFranchisees, setConnectingFranchisees] = useState(new Set());
  const [expandedFranchisees, setExpandedFranchisees] = useState(new Set());
  const [coveredAreas, setCoveredAreas] = useState({});
  const { confirmDialog, confirm } = useConfirm();
  const [showAreasFor, setShowAreasFor] = useState(null);
  const [areasAnchor, setAreasAnchor] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    companyName: '',
    companyRegNumber: '',
    vatNumber: '',
    role: 'technician',
    phone: '',
    mobile: '',
    addressLine1: '',
    addressLine2: '',
    addressCity: '',
    addressCounty: '',
    addressPostcode: '',
    assignedFranchiseeId: '',
    branchId: '',
    branchName: '',
    qbCustomerId: '',
    qbEnvironment: '',
    active: true
  });

  useEffect(() => {
    fetchUsers();
  }, [userProfile]);

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    if (type === 'error') toast.error(message);
    else if (type === 'warning') toast.info(message);
    else toast.success(message);
  };

  // Helper function to handle OAuth success (prevents duplicates)
  const handleOAuthSuccess = async (franchiseeId, source) => {
    if (oauthCompletedSessions.has(franchiseeId)) {
      console.log(`OAuth success already handled for franchisee ${franchiseeId}`);
      return;
    }
    
    setOauthCompletedSessions(prev => new Set([...prev, franchiseeId]));
    showNotification('QuickBooks successfully connected!');
    
    // Stop ngrok after successful connection
    try {
      await AdminService.stopNgrok();
      console.log('✅ Ngrok tunnel stopped');
    } catch (error) {
      console.warn('⚠️ Failed to stop ngrok:', error.message);
    }
    
    fetchUsers(); // Refresh the UI first
    
    // If we're editing this user, get their QB company ID and update the form
    if (editingUser && editingUser.id === franchiseeId) {
      try {
        // Wait a bit for the backend to finish processing
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Make direct API call to get connection details with companyId
        const response = await crudOperations.get(`/quickbooks/connection/${franchiseeId}`);
        if (response.data?.connected && response.data?.companyId) {
          setFormData(prev => ({
            ...prev,
            qbCustomerId: response.data.companyId
          }));
        }
      } catch (error) {
        console.error('❌ Error updating form qbCustomerId:', error);
      }
    }
    
    // Clear connecting state
    setConnectingFranchisees(prev => {
      const newSet = new Set(prev);
      newSet.delete(franchiseeId);
      return newSet;
    });
    
    // Clean up completed session after delay
    setTimeout(() => {
      setOauthCompletedSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(franchiseeId);
        return newSet;
      });
    }, 5000);
  };

  useEffect(() => {
    // Listen for OAuth completion messages from popup (postMessage)
    const handleMessage = async (event) => {
      // Only listen for messages from our own domain (localhost)
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'QB_OAUTH_COMPLETE') {
        const { success, sid, franchiseeId, message } = event.data;
        
        // Send ACK back to popup
        try {
          event.source.postMessage({
            type: 'QB_OAUTH_ACK',
            sid
          }, event.origin);
        } catch (ackError) {
          console.warn('Failed to send ACK:', ackError);
        }
        
        if (success) {
          console.log('✅ Received success via postMessage');
          // Use franchiseeId if available, fallback to sid for compatibility
          handleOAuthSuccess(franchiseeId || sid, 'postMessage');
        } else {
          showNotification(`Failed to complete QuickBooks connection: ${message}`, 'error');
        }
      }
    };

    // Listen for OAuth completion via BroadcastChannel
    const channel = new BroadcastChannel('qb-oauth');
    const handleBroadcast = (event) => {
      if (event.data.type === 'QB_OAUTH_COMPLETE') {
        const { success, sid, franchiseeId, message } = event.data;
        
        if (success) {
          console.log('✅ Received success via BroadcastChannel');
          handleOAuthSuccess(franchiseeId, 'BroadcastChannel');
        } else {
          showNotification(`Failed to complete QuickBooks connection: ${message}`, 'error');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    channel.addEventListener('message', handleBroadcast);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, []);

  const fetchUsers = async () => {
    try {
      // Fetch users from API
      const response = await userService.getUsers({
        limit: 100,
        active: undefined // Get both active and inactive users
      });
      
      const usersData = response.users || [];
      setAllUsers(usersData);
      
      // Filter users based on current user's role
      let filteredUsers;
      if (userProfile?.role === 'franchisee') {
        // Franchisees can only see their assigned technicians + themselves
        filteredUsers = usersData.filter(user => 
          user.id === userProfile.id || // Show themselves
          (user.role === 'technician' && user.assigned_franchisee_id === userProfile.id)
        );
      } else if (userProfile?.role === 'admin') {
        // Admins can see all users
        filteredUsers = usersData;
      } else {
        // Technicians can only see themselves
        filteredUsers = usersData.filter(user => user.id === userProfile.id);
      }
      
      // Sort users: active first, then inactive - only on initial load
      const sorted = [...filteredUsers].sort((a, b) => {
        if (a.is_active === b.is_active) return 0;
        return a.is_active ? -1 : 1;
      });
      
      setUsers(filteredUsers);
      setSortedUsers(sorted);

      // Initialize all franchisees as expanded (admin tree view)
      if (userProfile?.role === 'admin') {
        setExpandedFranchisees(prev => {
          // Only set if empty (first load) - don't reset user's expand/collapse state
          if (prev.size === 0) {
            return new Set(usersData.filter(u => u.role === 'franchisee').map(u => u.id));
          }
          return prev;
        });
      }

      // Check QuickBooks connections for franchisees (admin only)
      if (userProfile?.role === 'admin') {
        await checkQuickBooksConnections(usersData);
        // Fetch covered areas data
        try {
          const areas = await userService.getCoveredAreas();
          setCoveredAreas(areas || {});
        } catch (error) {
          console.warn('Could not fetch covered areas:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkQuickBooksConnections = async (usersList) => {
    if (userProfile?.role !== 'admin') return;
    
    const connections = {};
    for (const user of usersList) {
      if (user.role === 'franchisee') {
        try {
          const hasConnection = await QuickBooksService.hasConnection(user.id);
          connections[user.id] = hasConnection;
        } catch (error) {
          // Log warning but don't throw - gracefully handle QB not being configured
          console.warn(`Could not check QuickBooks connection for ${user.name} (${user.id}):`, error.message);
          connections[user.id] = false;
        }
      }
    }
    setQbConnections(connections);
  };

  // Close covered areas popover when clicking outside
  useEffect(() => {
    if (!showAreasFor) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.covered-areas-popover') && !e.target.closest('.covered-areas-btn')) {
        setShowAreasFor(null);
        setAreasAnchor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAreasFor]);

  // Toggle covered areas popover — capture button position for fixed placement
  const toggleAreasPopover = useCallback((userId, e) => {
    if (showAreasFor === userId) {
      setShowAreasFor(null);
      setAreasAnchor(null);
    } else {
      const btn = e.currentTarget.getBoundingClientRect();
      setAreasAnchor({ btnBottom: btn.bottom, btnTop: btn.top, btnRight: btn.right });
      setShowAreasFor(userId);
    }
  }, [showAreasFor]);

  // Ref callback: position popover after render so we know its actual height
  const popoverRefCallback = useCallback((el) => {
    if (!el || !areasAnchor) return;
    const pad = 8;
    const popH = el.offsetHeight;
    const popW = el.offsetWidth;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    // Vertical: try below the button first, flip above if no room, clamp as last resort
    let top;
    const spaceBelow = vh - areasAnchor.btnBottom - pad;
    const spaceAbove = areasAnchor.btnTop - pad;
    if (popH <= spaceBelow) {
      top = areasAnchor.btnBottom + 4;
    } else if (popH <= spaceAbove) {
      top = areasAnchor.btnTop - popH - 4;
    } else {
      // Doesn't fit either way — clamp to viewport
      top = pad;
    }

    // Horizontal: align right edge to button, but keep on-screen
    let right = vw - areasAnchor.btnRight;
    if (right < pad) right = pad;
    if (vw - right - popW < pad) right = vw - popW - pad;

    el.style.top = `${top}px`;
    el.style.right = `${right}px`;
    el.style.maxHeight = `${vh - pad * 2}px`;
  }, [areasAnchor]);

  // Render covered areas popover as a portal with fixed positioning
  const renderCoveredAreasPortal = () => {
    if (!showAreasFor || !areasAnchor) return null;
    const areas = coveredAreas[showAreasFor] || [];

    let content;
    if (areas.length === 0) {
      content = <p className="text-sm text-gray-500 italic">No coverage data</p>;
    } else {
      const grouped = {};
      areas.forEach(a => {
        if (!grouped[a.postcodeArea]) grouped[a.postcodeArea] = [];
        grouped[a.postcodeArea].push(a.town);
      });
      content = (
        <>
          <p className="text-sm font-semibold text-gray-700 mb-2">Post Towns Covered</p>
          {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([area, towns]) => (
            <div key={area} className="mb-1.5">
              <span className="text-sm font-bold text-blue-700">{area}: </span>
              <span className="text-sm text-gray-600">{towns.join(', ')}</span>
            </div>
          ))}
          <p className="text-xs text-gray-400 mt-2 pt-1 border-t border-gray-100">{areas.length} town{areas.length !== 1 ? 's' : ''} total</p>
        </>
      );
    }

    return ReactDOM.createPortal(
      <div
        ref={popoverRefCallback}
        style={{ position: 'fixed', top: 0, right: 0, zIndex: 9999 }}
        className="covered-areas-popover bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-[480px] overflow-y-auto"
      >
        {content}
      </div>,
      document.body
    );
  };

  const handleGeneratePassword = () => {
    const newPassword = userService.generateSecurePassword();
    setFormData(prev => ({ ...prev, password: newPassword }));
  };

  const handleConnectQuickBooks = async (franchiseeId) => {
    // Prevent multiple clicks
    if (connectingFranchisees.has(franchiseeId)) {
      console.log('Already connecting for franchisee:', franchiseeId);
      return;
    }
    
    try {
      // Set connecting state
      setConnectingFranchisees(prev => new Set([...prev, franchiseeId]));
      
      console.log('🔗 Starting QuickBooks connection for franchisee:', franchiseeId);
      
      // Check if ngrok is running, start if needed
      console.log('🔍 Checking ngrok status...');
      const ngrokStatus = await AdminService.getNgrokStatus();
      console.log('✅ ngrok status:', ngrokStatus);
      
      if (!ngrokStatus.running) {
        console.log('🌐 Starting ngrok for QuickBooks OAuth...');
        const startResult = await AdminService.startNgrok();
        if (!startResult.success) {
          throw new Error('Failed to start ngrok tunnel');
        }
        console.log('✅ ngrok started successfully');
        // Give ngrok a moment to start up
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      
      // Generate session ID
      const sid = crypto.randomUUID().slice(0, 8);
      
      // Get admin token for the backend endpoint
      const idToken = await AdminService.getIdToken();
      
      // Use server-side OAuth flow with sid
      const oauthUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:9003'}/qb/start?franchiseeId=${franchiseeId}&sid=${sid}&token=${encodeURIComponent(idToken)}`;
      
      // Open OAuth flow in a popup window
      const popup = window.open(
        oauthUrl,
        'qb',
        'width=480,height=720,scrollbars=yes,resizable=yes'
      );

      // Listen for broadcast message from popup
      const handleMessage = (event) => {
        // Accept messages from localhost (development) and ngrok domain (OAuth popup)
        const allowedOrigins = [
          'http://localhost:3000',
          'http://localhost:3001', 
          'https://grown-many-marlin.ngrok-free.app',
          'https://pestcontrol.co.uk'
        ];
        
        if (!allowedOrigins.includes(event.origin)) {
          console.log('🚫 Ignoring message from disallowed origin:', event.origin);
          return;
        }
        
        if (event.data.type === 'QUICKBOOKS_OAUTH_SUCCESS') {
          console.log('✅ QuickBooks OAuth successful via broadcast from:', event.origin);
          handleOAuthSuccess(event.data.franchiseeId, 'broadcast');
          if (popup && !popup.closed) {
            popup.close();
          }
          window.removeEventListener('message', handleMessage);
          clearTimeout(timeout);
        } else if (event.data.type === 'QUICKBOOKS_OAUTH_ERROR') {
          console.error('❌ QuickBooks OAuth failed:', event.data.error);
          showNotification(`Failed to complete QuickBooks connection: ${event.data.error}`, 'error');
          if (popup && !popup.closed) {
            popup.close();
          }
          window.removeEventListener('message', handleMessage);
          clearTimeout(timeout);
        }
      };

      window.addEventListener('message', handleMessage);

      // Set a timeout for OAuth completion (10 minutes matches server session timeout)
      const timeout = setTimeout(() => {
        console.log('⏱️ OAuth timeout - session expired');
        window.removeEventListener('message', handleMessage);
        if (popup && !popup.closed) {
          popup.close();
        }
        showNotification('QuickBooks connection timed out. Please try again.', 'warning');
        // Clear connecting state on timeout
        setConnectingFranchisees(prev => {
          const newSet = new Set(prev);
          newSet.delete(franchiseeId);
          return newSet;
        });
      }, 10 * 60 * 1000); // 10 minutes
      
    } catch (error) {
      console.error('Error initiating QuickBooks connection:', error);
      showNotification(`Failed to start QuickBooks connection: ${error.message}`, 'error');
      // Clear connecting state on error
      setConnectingFranchisees(prev => {
        const newSet = new Set(prev);
        newSet.delete(franchiseeId);
        return newSet;
      });
    }
  };

  const handleDisconnectQuickBooks = async (franchiseeId, franchiseeName) => {
    const ok = await confirm({
      title: 'Disconnect QuickBooks',
      message: `Disconnect QuickBooks for ${franchiseeName}? Existing data will be kept but sync will stop.`,
      variant: 'warning',
      confirmLabel: 'Disconnect'
    });
    if (!ok) return;

    try {
      await QuickBooksService.disconnect(franchiseeId);
      
      // Immediately update the connection status in state
      setQbConnections(prev => ({
        ...prev,
        [franchiseeId]: false
      }));
      
      // If we're currently editing this user, clear the qbCustomerId in the form
      if (editingUser && editingUser.id === franchiseeId) {
        setFormData(prev => ({
          ...prev,
          qbCustomerId: ''
        }));
      }
      
      showNotification(`Successfully disconnected QuickBooks for ${franchiseeName}`);
      
      // Refresh users data to ensure UI is up to date
      fetchUsers();
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error);
      showNotification(`Failed to disconnect QuickBooks: ${error.message}`, 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          email: formData.email,
          name: formData.name,
          company_name: formData.companyName || null,
          company_reg_number: formData.companyRegNumber || null,
          vat_number: formData.vatNumber || null,
          role: formData.role,
          phone: formData.phone,
          mobile: formData.mobile || null,
          address_line1: formData.addressLine1 || null,
          address_line2: formData.addressLine2 || null,
          address_city: formData.addressCity || null,
          address_county: formData.addressCounty || null,
          address_postcode: formData.addressPostcode || null,
          assigned_franchisee_id: formData.role === 'technician' ? formData.assignedFranchiseeId : null,
          branch_id: formData.role === 'franchisee' ? '1' : (formData.branchId || null),
          branch_name: formData.role === 'franchisee' ? (formData.branchName || null) : undefined,
          is_active: formData.active
        };

        // Add QB environment for franchisees (only if not already connected)
        if (formData.role === 'franchisee' && !formData.qbCustomerId) {
          updateData.qb_environment = formData.qbEnvironment;
        }
        
        // Include password only if a new one was provided
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }
        
        await userService.updateUser(editingUser.id, updateData);
      } else {
        // Create new user
        await userService.createUser({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          company_name: formData.companyName || null,
          company_reg_number: formData.companyRegNumber || null,
          vat_number: formData.vatNumber || null,
          role: formData.role,
          phone: formData.phone,
          mobile: formData.mobile || null,
          address_line1: formData.addressLine1 || null,
          address_line2: formData.addressLine2 || null,
          address_city: formData.addressCity || null,
          address_county: formData.addressCounty || null,
          address_postcode: formData.addressPostcode || null,
          assigned_franchisee_id: formData.role === 'technician' ? formData.assignedFranchiseeId : null,
          branch_id: formData.role === 'franchisee' ? '1' : (formData.branchId || null),
          branch_name: formData.role === 'franchisee' ? (formData.branchName || null) : undefined,
          is_active: true
        });
      }

      // Reset form and close modal
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'technician',
        phone: '',
        assignedFranchiseeId: '',
        branchId: '',
        branchName: '',
        qbCustomerId: '',
        active: true
      });
      setShowAddModal(false);
      setEditingUser(null);
      setShowPassword(false);
      showNotification(editingUser ? 'User updated successfully' : 'User created successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      showNotification('Error saving user: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (user) => {
    // Get QuickBooks Customer ID if user is a franchisee with active connection
    let qbCustomerId = user.qb_customer_id || '';
    if (user.role === 'franchisee') {
      try {
        const response = await crudOperations.get(`/quickbooks/connection/${user.id}`);
        if (response.data?.connected && response.data?.companyId) {
          qbCustomerId = response.data.companyId;
        }
      } catch (error) {
        console.warn('Could not fetch QB connection for user:', error.message);
      }
    }

    setFormData({
      email: user.email,
      password: '', // Don't show existing password
      name: user.name,
      companyName: user.company_name || '',
      companyRegNumber: user.company_reg_number || '',
      vatNumber: user.vat_number || '',
      role: user.role,
      phone: user.phone || '',
      mobile: user.mobile || '',
      addressLine1: user.address_line1 || '',
      addressLine2: user.address_line2 || '',
      addressCity: user.address_city || '',
      addressCounty: user.address_county || '',
      addressPostcode: user.address_postcode || '',
      assignedFranchiseeId: user.assigned_franchisee_id || '',
      branchId: user.branch_id || '',
      branchName: user.branch_name || '',
      qbCustomerId: qbCustomerId,
      qbEnvironment: user.qb_environment || '',
      active: user.is_active !== false // Default to true if undefined
    });
    setEditingUser(user);
    setShowAddModal(true);
  };

  const handleDelete = async (userId) => {
    const ok = await confirm({
      title: 'Delete User',
      message: 'This will permanently delete this user. This cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      await userService.deleteUser(userId);
      showNotification('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Error deleting user: ' + error.message, 'error');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await userService.updateUser(userId, {
        is_active: !currentStatus
      });
      
      // Update the sortedUsers array in place without reordering
      setSortedUsers(prevSorted => 
        prevSorted.map(user => 
          user.id === userId ? { ...user, is_active: !currentStatus } : user
        )
      );
      
      // Update the users array as well
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, is_active: !currentStatus } : user
        )
      );
    } catch (error) {
      console.error('Error updating user status:', error);
      showNotification('Error updating user status: ' + error.message, 'error');
    }
  };

  const handleLoginAsUser = async (user) => {
    const ok = await confirm({
      title: 'Switch User',
      message: `Login as ${user.name} (${user.email})? You will be switched to their account.`,
      variant: 'question',
      confirmLabel: 'Switch'
    });
    if (!ok) return;

    try {
      setLoading(true);
      
      // Actually login as the user
      await authService.loginAsUser(user);
      
      showNotification(`Now logged in as ${user.name}`);
      // The onAuthStateChanged in App.js will handle the redirect
      
    } catch (error) {
      console.error('Error logging in as user:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      if (error.response?.status === 403) {
        errorMessage = 'Access denied. You must be an admin to login as other users.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found or no longer exists.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again or contact support.';
      } else if (error.message.includes('fetch')) {
        errorMessage = 'Cannot connect to server. Please ensure the server is running.';
      }
      
      showNotification(`Failed to login as ${user.name}: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToAdmin = async () => {
    try {
      setLoading(true);
      await authService.returnToAdmin();
      showNotification('Returned to admin account successfully');
      // The onAuthStateChanged in App.js will handle the redirect
    } catch (error) {
      console.error('Error returning to admin:', error);
      showNotification('Failed to return to admin account: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'technician',
      phone: '',
      assignedFranchiseeId: '',
      branchId: '',
      branchName: '',
      qbCustomerId: '',
      active: true
    });
    setShowAddModal(false);
    setEditingUser(null);
    setShowPassword(false);
  };

  if (loading && users.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="hidden md:block text-3xl font-bold text-gray-900 mb-2">
            {userProfile?.role === 'franchisee' ? 'My Technicians' : 'User Management'}
          </h1>
          <p className="text-gray-600 text-xs md:text-base">
            {userProfile?.role === 'franchisee'
              ? 'View technicians assigned to your franchise'
              : userProfile?.role === 'admin'
              ? 'Manage technician accounts and permissions'
              : 'Your account information'
            }
          </p>
          {authService.hasAdminReturn() && userProfile?.role !== 'admin' && (
            <div className="mt-2 flex items-center space-x-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertCircle size={16} />
              <span className="text-sm font-medium">
                Logged in as {userProfile?.name} - Admin session available
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {authService.hasAdminReturn() && userProfile?.role !== 'admin' && (
            <button
              onClick={handleReturnToAdmin}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              disabled={loading}
            >
              <ArrowLeft size={20} />
              <span>Return to Admin</span>
            </button>
          )}
          {userProfile?.role === 'admin' && !authService.hasAdminReturn() && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
            >
              <Plus size={20} />
              <span>Add User</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {userProfile?.role === 'admin' ? 'User Accounts' : 
             userProfile?.role === 'franchisee' ? 'Technician Accounts' : 
             'Your Account'}
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {sortedUsers.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">
                {userProfile?.role === 'admin' ? 'No users added yet.' :
                 userProfile?.role === 'franchisee' ? 'No technicians assigned yet.' :
                 'No account information available.'}
              </p>
            </div>
          ) : userProfile?.role === 'admin' ? (
            /* ===== ADMIN TREE VIEW ===== */
            (() => {
              const adminUsers = sortedUsers.filter(u => u.role === 'admin');
              const franchisees = sortedUsers.filter(u => u.role === 'franchisee').sort((a, b) => a.name.localeCompare(b.name));
              const assignedTechIds = new Set();
              const techsByFranchisee = {};
              franchisees.forEach(f => {
                techsByFranchisee[f.id] = allUsers
                  .filter(u => u.role === 'technician' && u.assigned_franchisee_id === f.id)
                  .sort((a, b) => a.name.localeCompare(b.name));
                techsByFranchisee[f.id].forEach(t => assignedTechIds.add(t.id));
              });
              const unassignedTechs = sortedUsers.filter(u => u.role === 'technician' && !assignedTechIds.has(u.id));

              const toggleFranchisee = (id) => {
                setExpandedFranchisees(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              };

              const renderUserRow = (user, indent = false) => (
                <div key={user.id} className={`p-3 md:p-4 ${indent ? 'pl-4 md:pl-12 ml-1 md:ml-4' : ''} ${!user.is_active ? 'opacity-50' : ''}`}>
                  {/* Desktop layout */}
                  <div className="hidden md:flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-4 min-w-0 flex-1">
                      <div className={`p-2 rounded-full flex-shrink-0 ${
                        !user.is_active ? 'bg-gray-400' :
                        user.role === 'admin' ? 'bg-red-600' :
                        user.role === 'franchisee' ? 'bg-purple-600' :
                        'bg-green-600'
                      }`}>
                        <User className="text-white" size={20} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`text-lg font-semibold ${!user.is_active ? 'text-gray-500' : 'text-gray-900'}`}>{user.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            user.role === 'admin' ? 'bg-red-100 text-red-800' : user.role === 'franchisee' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                          }`}>{user.role}</span>
                        </div>
                        <p className={`text-sm truncate ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.email}</p>
                        {user.branch_name && (
                          <p className={`text-xs ${!user.is_active ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.role === 'franchisee' ? `Code: ${user.branch_name}` : `Branch ${user.branch_id}:${user.branch_name}`}
                          </p>
                        )}
                        {user.phone && <p className={`text-sm ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.phone}</p>}
                        {user.mobile && <p className={`text-sm ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.mobile}</p>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {coveredAreas[user.id] && (
                        <button onClick={(e) => toggleAreasPopover(user.id, e)} className="covered-areas-btn p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors" title="View covered areas"><Info size={18} /></button>
                      )}
                      {user.role !== 'admin' && (
                        <button onClick={() => handleToggleActive(user.id, user.is_active)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${user.is_active ? 'bg-green-600' : 'bg-gray-300'}`} title={user.is_active ? 'Deactivate' : 'Activate'}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button onClick={() => handleLoginAsUser(user)} className="text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-green-100 transition-colors" title={`Login as ${user.name}`} disabled={!user.is_active}><LogIn size={14} /><span>Switch Session</span></button>
                      )}
                      <button onClick={() => handleEdit(user)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit"><Edit2 size={18} /></button>
                      <button onClick={() => handleDelete(user.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><Trash2 size={18} /></button>
                    </div>
                  </div>
                  {/* Mobile layout */}
                  <div className="md:hidden">
                    <div className="flex items-stretch justify-between gap-2">
                      <div className="flex items-start space-x-2 min-w-0 flex-1">
                        <div className={`p-1.5 rounded-full flex-shrink-0 ${
                          !user.is_active ? 'bg-gray-400' :
                          user.role === 'admin' ? 'bg-red-600' :
                          user.role === 'franchisee' ? 'bg-purple-600' :
                          'bg-green-600'
                        }`}>
                          <User className="text-white" size={14} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h3 className={`text-xs font-semibold ${!user.is_active ? 'text-gray-500' : 'text-gray-900'}`}>{user.name}</h3>
                            {user.role !== 'admin' && (
                              <button
                                onClick={() => handleLoginAsUser(user)}
                                className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-green-100 transition-colors"
                                title={`Login as ${user.name}`}
                                disabled={!user.is_active}
                              >
                                <LogIn size={11} />
                                <span>Switch Session</span>
                              </button>
                            )}
                          </div>
                          <p className={`text-xs truncate ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.email}</p>
                          {user.branch_id && (
                            <p className={`text-xs ${!user.is_active ? 'text-gray-400' : 'text-gray-500'}`}>
                              Branch {user.branch_id}{user.branch_name ? ` (${user.branch_name})` : ''}
                            </p>
                          )}
                          {user.phone && <p className={`text-xs ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.phone}</p>}
                          {user.mobile && <p className={`text-xs ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.mobile}</p>}
                        </div>
                      </div>
                      {user.role !== 'admin' && (
                        <div className="flex flex-col items-end justify-between flex-shrink-0">
                          <button onClick={() => handleToggleActive(user.id, user.is_active)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${user.is_active ? 'bg-green-600' : 'bg-gray-300'}`} title={user.is_active ? 'Deactivate' : 'Activate'}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                          <div className="flex space-x-2">
                            {coveredAreas[user.id] && (
                              <button onClick={(e) => toggleAreasPopover(user.id, e)} className="covered-areas-btn w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors" title="View covered areas"><Info size={14} /></button>
                            )}
                            <button onClick={() => handleEdit(user)} className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors" title="Edit"><Edit2 size={16} /></button>
                            <button onClick={() => handleDelete(user.id)} className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors" title="Delete"><Trash2 size={16} /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );

              return (
                <>
                  {/* Admin users */}
                  {adminUsers.map(user => renderUserRow(user))}

                  {/* Franchisees with nested technicians */}
                  {franchisees.map(franchisee => {
                    const techs = techsByFranchisee[franchisee.id] || [];
                    const isExpanded = expandedFranchisees.has(franchisee.id);
                    return (
                      <div key={franchisee.id}>
                        {/* Franchisee header row */}
                        <div className={`${!franchisee.is_active ? 'opacity-50' : ''}`}>
                          <div className="p-3 md:p-4">
                            {/* Desktop layout */}
                            <div className="hidden md:flex items-center justify-between gap-2">
                              <div className="flex items-center space-x-4 min-w-0 flex-1">
                                <div className={`p-2 rounded-full flex-shrink-0 ${!franchisee.is_active ? 'bg-gray-400' : 'bg-purple-600'}`}>
                                  <User className="text-white" size={20} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {techs.length > 0 && (
                                      <button onClick={() => toggleFranchisee(franchisee.id)} className="p-0.5 hover:bg-gray-100 rounded transition-colors flex-shrink-0" title={isExpanded ? 'Collapse' : 'Expand'}>
                                        {isExpanded ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
                                      </button>
                                    )}
                                    <h3 className={`text-lg font-semibold ${!franchisee.is_active ? 'text-gray-500' : 'text-gray-900'}`}>
                                      {franchisee.name}{franchisee.company_name && <span className="font-normal text-gray-500"> — {franchisee.company_name}</span>}
                                    </h3>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">franchisee</span>
                                    {techs.length > 0 && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex items-center space-x-1">
                                        <Users size={12} />
                                        <span>{techs.length} technician{techs.length !== 1 ? 's' : ''}</span>
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm truncate ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{franchisee.email}</p>
                                  {franchisee.branch_name && (
                                    <p className={`text-xs ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Code: {franchisee.branch_name}
                                    </p>
                                  )}
                                  {franchisee.phone && <p className={`text-sm ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{franchisee.phone}</p>}
                                  {franchisee.mobile && <p className={`text-sm ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{franchisee.mobile}</p>}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {coveredAreas[franchisee.id] && (
                                  <button onClick={(e) => toggleAreasPopover(franchisee.id, e)} className="covered-areas-btn p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors" title="View covered areas"><Info size={18} /></button>
                                )}
                                <button onClick={() => handleToggleActive(franchisee.id, franchisee.is_active)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${franchisee.is_active ? 'bg-green-600' : 'bg-gray-300'}`} title={franchisee.is_active ? 'Deactivate' : 'Activate'}>
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${franchisee.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                                <button onClick={() => handleLoginAsUser(franchisee)} className="text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-green-100 transition-colors" title={`Login as ${franchisee.name}`} disabled={!franchisee.is_active}><LogIn size={14} /><span>Switch Session</span></button>
                                <button onClick={() => handleEdit(franchisee)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit"><Edit2 size={18} /></button>
                                <button onClick={() => handleDelete(franchisee.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><Trash2 size={18} /></button>
                              </div>
                            </div>
                            {/* Mobile layout */}
                            <div className="md:hidden">
                              <div className="flex items-stretch justify-between gap-2">
                                <div className="flex items-start space-x-2 min-w-0 flex-1">
                                  <div className="flex flex-col items-center flex-shrink-0 gap-1">
                                    <div className={`p-1.5 rounded-full ${!franchisee.is_active ? 'bg-gray-400' : 'bg-purple-600'}`}>
                                      <User className="text-white" size={14} />
                                    </div>
                                    {techs.length > 0 && (
                                      <button onClick={() => toggleFranchisee(franchisee.id)} className="p-0.5 hover:bg-gray-100 rounded transition-colors" title={isExpanded ? 'Collapse' : 'Expand'}>
                                        {isExpanded ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                                      </button>
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <h3 className={`text-xs font-semibold ${!franchisee.is_active ? 'text-gray-500' : 'text-gray-900'}`}>{franchisee.name}</h3>
                                      <button
                                        onClick={() => handleLoginAsUser(franchisee)}
                                        className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-green-100 transition-colors"
                                        title={`Login as ${franchisee.name}`}
                                        disabled={!franchisee.is_active}
                                      >
                                        <LogIn size={11} />
                                        <span>Switch Session</span>
                                      </button>
                                      {techs.length > 0 && (
                                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 flex items-center space-x-1">
                                          <Users size={10} />
                                          <span>{techs.length}</span>
                                        </span>
                                      )}
                                    </div>
                                    {franchisee.company_name && <p className="text-xs text-gray-500">{franchisee.company_name}</p>}
                                    <p className={`text-xs truncate ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{franchisee.email}</p>
                                    {franchisee.branch_id && (
                                      <p className={`text-xs ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Branch {franchisee.branch_id}{franchisee.branch_name ? ` (${franchisee.branch_name})` : ''}
                                      </p>
                                    )}
                                    {franchisee.phone && <p className={`text-xs ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{franchisee.phone}</p>}
                                    {franchisee.mobile && <p className={`text-xs ${!franchisee.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{franchisee.mobile}</p>}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end justify-between flex-shrink-0">
                                  <button onClick={() => handleToggleActive(franchisee.id, franchisee.is_active)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${franchisee.is_active ? 'bg-green-600' : 'bg-gray-300'}`} title={franchisee.is_active ? 'Deactivate' : 'Activate'}>
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${franchisee.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                                  </button>
                                  <div className="flex space-x-2">
                                    {coveredAreas[franchisee.id] && (
                                      <button onClick={(e) => toggleAreasPopover(franchisee.id, e)} className="covered-areas-btn w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors" title="View covered areas"><Info size={14} /></button>
                                    )}
                                    <button onClick={() => handleEdit(franchisee)} className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors" title="Edit"><Edit2 size={16} /></button>
                                    <button onClick={() => handleDelete(franchisee.id)} className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors" title="Delete"><Trash2 size={16} /></button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Nested technicians */}
                        {isExpanded && techs.length > 0 && (
                          <div className="bg-gray-50">
                            {techs.map(tech => renderUserRow(tech, true))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Unassigned technicians */}
                  {unassignedTechs.length > 0 && (
                    <div>
                      <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400">
                        <h4 className="text-sm font-semibold text-yellow-800 flex items-center space-x-2">
                          <AlertCircle size={16} />
                          <span>Unassigned Technicians ({unassignedTechs.length})</span>
                        </h4>
                      </div>
                      {unassignedTechs.map(tech => renderUserRow(tech))}
                    </div>
                  )}
                </>
              );
            })()
          ) : (
            /* ===== NON-ADMIN FLAT LIST (franchisee/technician views) ===== */
            sortedUsers.map((user) => (
              <div key={user.id} className={`p-3 md:p-4 ${!user.is_active ? 'opacity-50' : ''}`}>
                <div className="flex items-start md:items-center justify-between gap-2">
                  <div className="flex items-start md:items-center space-x-3 md:space-x-4 min-w-0 flex-1">
                    <div className={`p-1.5 md:p-2 rounded-full flex-shrink-0 ${!user.is_active ? 'bg-gray-400' : 'bg-blue-600'}`}>
                      <User className="text-white" size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`text-sm md:text-lg font-semibold ${!user.is_active ? 'text-gray-500' : 'text-gray-900'}`}>{user.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.role === 'admin'
                            ? 'bg-red-100 text-red-800'
                            : user.role === 'franchisee'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {user.role}
                        </span>
                      </div>
                      <p className={`text-xs md:text-sm truncate ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>
                        {user.email}
                      </p>
                      {user.phone && (
                        <p className={`text-xs md:text-sm ${!user.is_active ? 'text-gray-400' : 'text-gray-600'}`}>{user.phone}</p>
                      )}
                      {userProfile?.role === 'admin' && user.role === 'technician' && user.assigned_franchisee_id && (
                        <div className="mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            !user.is_active ? 'bg-gray-200 text-gray-500' : 'bg-blue-100 text-blue-800'
                          }`}>
                            Assigned to: {allUsers.find(u => u.id === user.assigned_franchisee_id)?.name || 'Unknown Franchisee'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {userProfile?.role === 'franchisee' && (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded flex-shrink-0">
                      Read Only
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) { setShowAddModal(false); setEditingUser(null); } }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Full Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="technician">Technician</option>
                  <option value="franchisee">Franchisee</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text" inputMode="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                  <input
                    type="text" inputMode="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mobile number"
                  />
                </div>
              </div>

              {formData.role === 'franchisee' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Legal company name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Reg No.</label>
                      <input
                        type="text"
                        value={formData.companyRegNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, companyRegNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. 08626078"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                      <input
                        type="text"
                        value={formData.vatNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. 167474966"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData(prev => ({ ...prev, addressLine1: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Address line 1"
                />
              </div>
              <input
                type="text"
                value={formData.addressLine2}
                onChange={(e) => setFormData(prev => ({ ...prev, addressLine2: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Address line 2"
              />
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  value={formData.addressCity}
                  onChange={(e) => setFormData(prev => ({ ...prev, addressCity: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="City/Town"
                />
                <input
                  type="text"
                  value={formData.addressCounty}
                  onChange={(e) => setFormData(prev => ({ ...prev, addressCounty: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="County"
                />
                <input
                  type="text"
                  value={formData.addressPostcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, addressPostcode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Postcode"
                />
              </div>

              {formData.role === 'technician' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Franchisee</label>
                  <select
                    value={formData.assignedFranchiseeId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedFranchisee = allUsers.find(u => u.id === selectedId);
                      setFormData(prev => ({
                        ...prev,
                        assignedFranchiseeId: selectedId,
                        branchName: selectedFranchisee?.branch_name || ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No Franchisee Assigned</option>
                    {allUsers.filter(user => user.role === 'franchisee').map(franchisee => (
                      <option key={franchisee.id} value={franchisee.id}>
                        {franchisee.name}{franchisee.branch_name ? ` (${franchisee.branch_name})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Only technicians can be assigned to franchisees
                  </p>
                </div>
              )}

              {formData.role === 'franchisee' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                  <input
                    type="text"
                    value={formData.branchName}
                    onChange={(e) => setFormData(prev => ({ ...prev, branchName: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. LE, RH, TN"
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    2-letter franchise code used in QuickBooks locations (e.g. "Branch 1:LE"). All technicians inherit this code.
                  </p>
                </div>
              )}

              {formData.role === 'technician' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Number</label>
                    <select
                      value={formData.branchId}
                      onChange={(e) => setFormData(prev => ({ ...prev, branchId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select branch number</option>
                      {Array.from({ length: 29 }, (_, i) => i + 2).map(n => (
                        <option key={n} value={String(n)}>{n}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      QB location: Branch {formData.branchId || '?'}:{formData.branchName || '??'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                    <input
                      type="text"
                      value={formData.branchName}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                      placeholder="Set on franchisee"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Inherited from assigned franchisee
                    </p>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  {!editingUser && (
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Generate Password
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={editingUser ? "Leave blank to keep current password" : "Password"}
                    required={!editingUser}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-8 overflow-y-auto">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      )}

      {renderCoveredAreasPortal()}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default UserManagement;